<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  assistants: { type: Array, default: () => [] },
  modelValue: { type: [Number, String], default: '' },
  isLoading: { type: Boolean, default: false },
  loadError: { type: String, default: '' },
});

const emit = defineEmits(['update:modelValue', 'retry']);
const { t } = useI18n();

const selectedValue = computed(() => String(props.modelValue || ''));

const handleChange = event => {
  const assistantId = Number(event.target.value);
  if (Number.isFinite(assistantId) && assistantId > 0) {
    emit('update:modelValue', assistantId);
  }
};
</script>

<template>
  <section
    data-testid="tools-agent-context"
    class="flex flex-col gap-4 rounded-xl border border-n-weak bg-n-alpha-1 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5"
  >
    <div class="flex min-w-0 items-start gap-3">
      <span
        class="i-lucide-bot mt-0.5 size-5 shrink-0 text-n-blue-10"
        aria-hidden="true"
      />
      <div class="min-w-0">
        <h2 class="text-sm font-semibold text-n-slate-12">
          {{ t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.AGENT_CONTEXT_TITLE') }}
        </h2>
        <p class="mt-1 text-sm leading-relaxed text-n-slate-11">
          {{ t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.AGENT_CONTEXT_DESCRIPTION') }}
        </p>
      </div>
    </div>

    <div class="flex w-full shrink-0 flex-col gap-2 sm:w-80">
      <label
        for="captain-tools-assistant"
        class="text-xs font-medium text-n-slate-11"
      >
        {{ t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.SELECT_AGENT') }}
      </label>
      <div class="relative w-full">
        <select
          id="captain-tools-assistant"
          data-testid="tools-agent-picker"
          :value="selectedValue"
          :disabled="isLoading || assistants.length === 0"
          class="h-10 w-full appearance-none rounded-lg border-0 bg-n-surface-1 py-2 pl-3 pr-10 text-sm text-n-slate-12 outline outline-1 -outline-offset-1 outline-n-weak transition-colors hover:outline-n-slate-6 focus:outline-n-blue-9 disabled:cursor-not-allowed disabled:bg-n-slate-2 disabled:text-n-slate-10"
          @change="handleChange"
        >
          <option v-if="isLoading" value="" disabled>
            {{ t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.LOADING_AGENTS') }}
          </option>
          <option v-else-if="assistants.length === 0" value="" disabled>
            {{ t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.NO_AGENTS') }}
          </option>
          <option
            v-for="assistant in assistants"
            :key="assistant.id"
            :value="String(assistant.id)"
          >
            {{ assistant.name }}
          </option>
        </select>
        <span
          class="i-lucide-chevron-down pointer-events-none absolute right-3 top-1/2 size-4 -translate-y-1/2 text-n-slate-11"
          aria-hidden="true"
        />
      </div>
      <div
        v-if="loadError"
        role="alert"
        class="flex flex-col gap-2 rounded-lg bg-n-ruby-2 p-3 text-sm text-n-ruby-11 sm:flex-row sm:items-center sm:justify-between"
      >
        <span class="break-words">{{ loadError }}</span>
        <Button
          type="button"
          color="slate"
          variant="faded"
          size="xs"
          icon="i-lucide-refresh-cw"
          :label="t('CAPTAIN.CUSTOM_TOOLS.MANAGEMENT.RETRY_AGENTS')"
          class="w-full shrink-0 sm:w-auto"
          @click="emit('retry')"
        />
      </div>
    </div>
  </section>
</template>
